
import { Component, Vue } from 'vue-property-decorator'
import StatCard from './stat_card.vue'
import { ContactService } from '@/services/index'
import * as echarts from 'echarts'

interface IStatData {
    total_amount: number
    order_count: number
    start_time?: number
    end_time?: number
}

interface IFilterForm {
    start_date: Date | null
    end_date: Date | null
    product: string
    username: string
    pay_type: string
    status: number | null
}

@Component({
    components: {
        StatCard
    }
})
export default class OrderStatistics extends Vue {
    private errorMsg = ''
    private chartMonth: any = null
    private chartYear: any = null

    private statData: IStatData = {
        total_amount: 0,
        order_count: 0
    }

    private filterForm: IFilterForm = {
        start_date: null,
        end_date: null,
        product: '',
        username: '',
        pay_type: '',
        status: 1
    }

    /* ---------------------- 分界线 ---------------------- */
    private mounted() {
        this.initializeDateRange()
        this.onQueryStatistics()
        window.addEventListener('resize', this.handleWindowResize)
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.handleWindowResize)
        if (this.chartMonth) {
            this.chartMonth.dispose()
        }
        if (this.chartYear) {
            this.chartYear.dispose()
        }
    }

    private handleWindowResize = () => {
        if (this.chartMonth) {
            this.chartMonth.resize()
        }
        if (this.chartYear) {
            this.chartYear.resize()
        }
    }

    /** 初始化日期范围 - 默认当前月份 */
    private initializeDateRange() {
        const now = new Date()
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        this.filterForm.start_date = firstDay
        this.filterForm.end_date = now
    }

    /** 查询统计数据 */
    private async onQueryStatistics() {
        if (!this.filterForm.start_date || !this.filterForm.end_date) {
            this.$message.warning('请选择开始日期和结束日期')
            return
        }

        if (this.filterForm.start_date > this.filterForm.end_date) {
            this.$message.warning('开始日期不能大于结束日期')
            return
        }

        this.errorMsg = ''

        try {
            // 转换日期为时间戳（秒）
            const start_time = Math.floor(this.filterForm.start_date.getTime() / 1000)
            const end_time = Math.floor(this.filterForm.end_date.getTime() / 1000)

            const params: any = {
                start_time,
                end_time
            }

            // 添加可选参数
            if (this.filterForm.product) {
                params.product = this.filterForm.product
            }
            if (this.filterForm.username) {
                params.username = this.filterForm.username
            }
            if (this.filterForm.pay_type) {
                params.pay_type = this.filterForm.pay_type
            }
            if (this.filterForm.status !== null) {
                params.status = this.filterForm.status
            }

            const result = await ContactService.getOrderAmountStat(params)

            if (result.status === 200 && result.data) {
                this.statData = {
                    total_amount: result.data.total_amount || 0,
                    order_count: result.data.order_count || 0,
                    start_time: result.data.start_time,
                    end_time: result.data.end_time
                }
                this.$message.success('数据查询成功')

                // 加载图表数据
                await this.$nextTick()
                this.loadCharts(params)
            } else {
                const msg = result.data && result.data.msg ? result.data.msg : '数据查询失败'
                this.errorMsg = msg
            }
        } catch (error) {
            this.errorMsg = '查询失败，请稍后重试'
            console.error('订单统计查询失败:', error)
        }
    }

    /** 加载图表数据 */
    private async loadCharts(baseParams: any) {
        try {
            // 加载最近一个月的数据
            await this.loadMonthChart(baseParams)
            // 加载最近一年的数据
            await this.loadYearChart(baseParams)
        } catch (error) {
            console.error('加载图表失败:', error)
        }
    }

    /** 加载一个月的柱状图 */
    private async loadMonthChart(baseParams: any) {
        const now = new Date()
        const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())

        const start_time = Math.floor(oneMonthAgo.getTime() / 1000)
        const end_time = Math.floor(now.getTime() / 1000)
        const time_diff = end_time - start_time

        const params = {
            ...baseParams,
            start_time,
            end_time,
            time_diff
        }

        const result = await ContactService.getOrderAmountStatByInterval(params)

        if (result.status === 200 && result.data && result.data.interval_data) {
            this.renderMonthChart(result.data.interval_data)
        }
    }

    /** 加载一年的柱状图 */
    private async loadYearChart(baseParams: any) {
        const now = new Date()
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())

        const start_time = Math.floor(oneYearAgo.getTime() / 1000)
        const end_time = Math.floor(now.getTime() / 1000)
        const time_diff = 31536000 // 一年的秒数

        const params = {
            ...baseParams,
            start_time,
            end_time,
            time_diff
        }

        const result = await ContactService.getOrderAmountStatByInterval(params)

        if (result.status === 200 && result.data && result.data.interval_data) {
            this.renderYearChart(result.data.interval_data)
        }
    }

    /** 渲染一个月的柱状图 */
    private renderMonthChart(data: any[]) {
        const chartEl = this.$refs.chartMonth as HTMLElement
        if (!chartEl) return

        const periods = data.map(item => item.period)
        const amounts = data.map(item => item.amount)

        if (!this.chartMonth) {
            this.chartMonth = echarts.init(chartEl)
        }

        const option = {
            tooltip: {
                trigger: 'axis',
                formatter: '{b}: ¥{c}'
            },
            grid: {
                left: '3%',
                right: '3%',
                bottom: '3%',
                top: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: periods,
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            },
            yAxis: {
                type: 'value',
                name: '金额(¥)'
            },
            series: [
                {
                    data: amounts,
                    type: 'bar',
                    itemStyle: {
                        color: '#409eff'
                    }
                }
            ]
        }

        this.chartMonth.setOption(option)
    }

    /** 渲染一年的柱状图 */
    private renderYearChart(data: any[]) {
        const chartEl = this.$refs.chartYear as HTMLElement
        if (!chartEl) return

        const periods = data.map(item => item.period)
        const amounts = data.map(item => item.amount)

        if (!this.chartYear) {
            this.chartYear = echarts.init(chartEl)
        }

        const option = {
            tooltip: {
                trigger: 'axis',
                formatter: '{b}: ¥{c}'
            },
            grid: {
                left: '3%',
                right: '3%',
                bottom: '3%',
                top: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: periods,
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            },
            yAxis: {
                type: 'value',
                name: '金额(¥)'
            },
            series: [
                {
                    data: amounts,
                    type: 'bar',
                    itemStyle: {
                        color: '#67c23a'
                    }
                }
            ]
        }

        this.chartYear.setOption(option)
    }

    /** 筛选条件变化 */
    private onFilterChange() {
        // 实时响应筛选条件变化
    }

    /** 重置筛选条件 */
    private onResetFilter() {
        this.filterForm = {
            start_date: null,
            end_date: null,
            product: '',
            username: '',
            pay_type: '',
            status: null
        }
        this.errorMsg = ''
        this.statData = {
            total_amount: 0,
            order_count: 0
        }
        this.initializeDateRange()
    }
}
